/**
 * useTonWallet — единственный hook для signing TON-транзакций.
 *
 * Public API:
 *   const wallet = useTonWallet();
 *   wallet.state    : WalletState — discriminated union, 7 вариантов (совместимость с Privy-era Token.tsx)
 *   wallet.address  : string | undefined
 *   wallet.signAndSend(opts): Promise<void> — пишет результат в state
 *   wallet.reset()  : void
 *
 * Внутренности:
 *   - Использует TonConnect через useTonConnectUI / useTonAddress / useTonWallet hooks
 *   - На request строит SendTransactionRequest через ton-connect.ts helper
 *   - TonConnect UI открывает approval modal/return-to-wallet, юзер подтверждает,
 *     wallet подписывает и сам отправляет в сеть
 *
 * Состояния:
 *   - not_ready: TonConnect провайдер ещё не загрузился
 *   - no_wallet: провайдер готов, но wallet не подключён (нужен ConnectButton)
 *   - ready: всё готово, можно signAndSend
 *   - signing: запрос подписи отправлен в wallet, ждём апрува юзера
 *   - submitting: юзер апрувнул, wallet отправляет в сеть (мы не различаем
 *     эту фазу от signing через TonConnect API, но держим состояние
 *     для совместимости с UI Token.tsx, который различает signing/submitting)
 *   - success: транзакция отправлена в сеть
 *   - error: что-то пошло не так
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useTonAddress,
  useTonConnectUI,
  useTonWallet as useTonConnectWallet,
} from '@tonconnect/ui-react';
import { Address, Cell } from '@ton/core';
import { buildSendRequest } from '../lib/ton-connect';

export type WalletState =
  | { kind: 'not_ready' }
  | { kind: 'no_wallet' }
  | { kind: 'ready'; address: string }
  | { kind: 'signing' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

export interface SignAndSendOpts {
  to: Address;
  value: bigint;
  body?: Cell;
  bounce?: boolean;
}

const TRANSIENT_KINDS: ReadonlySet<WalletState['kind']> = new Set([
  'signing',
  'submitting',
  'success',
  'error',
]);

function getNetwork(): 'testnet' | 'mainnet' {
  const env = import.meta.env.VITE_TON_NETWORK as string | undefined;
  return env === 'mainnet' ? 'mainnet' : 'testnet';
}

export function useTonWallet() {
  const [tonConnectUI] = useTonConnectUI();
  const tcWallet = useTonConnectWallet();
  const userAddress = useTonAddress();
  const network = getNetwork();

  const [state, setState] = useState<WalletState>({ kind: 'not_ready' });
  const inFlightRef = useRef(false);

  // Sync state с TonConnect connection status.
  // Не трогаем transient states (signing/submitting/success/error) во время операции.
  useEffect(() => {
    if (inFlightRef.current) return;

    setState((prev) => {
      if (TRANSIENT_KINDS.has(prev.kind)) return prev;

      // Provider не готов — tonConnectUI может быть undefined первое время
      if (!tonConnectUI) return { kind: 'not_ready' };

      // Wallet не подключён
      if (!tcWallet || !userAddress) return { kind: 'no_wallet' };

      // Connected → ready
      return { kind: 'ready', address: userAddress };
    });
  }, [tonConnectUI, tcWallet, userAddress]);

  const signAndSend = useCallback(
    async (opts: SignAndSendOpts): Promise<void> => {
      if (!tcWallet || !userAddress) {
        setState({ kind: 'error', message: 'wallet not connected' });
        return;
      }

      inFlightRef.current = true;
      setState({ kind: 'signing' });

      try {
        const request = buildSendRequest({
          to: opts.to,
          value: opts.value,
          body: opts.body,
          bounce: opts.bounce ?? true,
          network,
        });

        // Промежуточный переход в 'submitting' — UI может показать
        // "sending to network" вместо "waiting for signature".
        // Реально TonConnect не сигналит когда юзер apruveнул, поэтому
        // переход условный — после короткой задержки или сразу после await.
        // Простое решение: оставляем 'signing' до resolve, потом 'success'.
        // Для UX можно перевести в submitting сразу после approval, но это
        // потребовало бы отдельных event listeners.
        await tonConnectUI.sendTransaction(request);

        setState({ kind: 'submitting' });
        // Маленькая задержка для UX — submitting флэш ~200ms
        await new Promise((r) => setTimeout(r, 200));
        setState({ kind: 'success' });
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message).slice(0, 200)
            : 'sign-and-send failed';
        setState({ kind: 'error', message });
      } finally {
        inFlightRef.current = false;
      }
    },
    [tcWallet, userAddress, tonConnectUI, network]
  );

  const reset = useCallback(() => {
    if (!tonConnectUI) {
      setState({ kind: 'not_ready' });
    } else if (!tcWallet || !userAddress) {
      setState({ kind: 'no_wallet' });
    } else {
      setState({ kind: 'ready', address: userAddress });
    }
  }, [tonConnectUI, tcWallet, userAddress]);

  return {
    state,
    address: userAddress || undefined,
    signAndSend,
    reset,
  };
}
